import { Box, BoxProps, Card, Stack } from "@mui/material";
import { DebounceSettings, debounce, isNumber } from "lodash-es";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { SignatureCanvas } from "react-signature-canvas";

import { FileHelper } from "@/common/helpers/file";
import { SvgHelper } from "@/common/helpers/svg";
import { useValueMemoWithDeepCompare } from "@/common/hooks/memo/useValueMemoWithDeepCompare";

import AppButton from "../Button/AppButton";
import AppIcon from "../Icons/AppIcon";

// Docs:
// https://github.com/agilgur5/react-signature-canvas
// https://github.com/agilgur5/trim-canvas
// https://github.com/szimek/signature_pad

interface NewValue {
  dataUrl: string;
  file: File;
}

interface Props {
  /** Default signature SVG image. Provide either value. */
  defaultImage?: {
    url?: string;
    dataUrl?: string;
    blob?: Blob;
    file?: File;
  };
  fileName?: string;
  withClear?: boolean;
  debounceOptions?: {
    wait: number;
    options?: DebounceSettings;
  };
  canvasProps?: React.CanvasHTMLAttributes<HTMLCanvasElement>;
  sx?: BoxProps["sx"];
  onClear?: () => void;
  /** Fired when user finishes drawing. */
  onChange?: (newValue: NewValue) => void;
  onChangeDebounced?: (newValue: NewValue) => void;
}

/** Signature canvas wrapper.
 * Input and output is in SVG format.
 */
export default function AppSignatureCanvas({
  defaultImage,
  fileName = "signature.svg",
  withClear = true,
  debounceOptions,
  canvasProps,
  sx,
  onClear,
  onChange,
  onChangeDebounced,
}: Props) {
  const sigCanvasRef = useRef<SignatureCanvas | null>(null);

  const debounceOptionsMemoized = useValueMemoWithDeepCompare(debounceOptions);

  const canvasPropsComputed = useMemo<React.CanvasHTMLAttributes<HTMLCanvasElement>>(
    () => ({
      width: 200,
      height: 100,
      className: "sigCanvas",
      ...canvasProps,
    }),
    [canvasProps],
  );
  const canvasSize = useMemo(
    () => ({
      width: isNumber(canvasPropsComputed.width) ? canvasPropsComputed.width || 200 : 200,
      height: isNumber(canvasPropsComputed.height) ? canvasPropsComputed.height || 100 : 100,
    }),
    [canvasPropsComputed],
  );

  // load default image
  useEffect(() => {
    (async () => {
      const defaultDataUrl = defaultImage?.url
        ? await FileHelper.fileUrlToDataUrl(defaultImage.url)
        : defaultImage?.dataUrl
          ? defaultImage?.dataUrl
          : defaultImage?.blob
            ? await FileHelper.blobToDataUrl(defaultImage.blob)
            : defaultImage?.file
              ? await FileHelper.fileToDataUrl(defaultImage.file)
              : null;
      if (defaultDataUrl) {
        sigCanvasRef.current?.fromDataURL(defaultDataUrl);
      }
    })();
  }, [sigCanvasRef.current]);

  const handleChangeDebounced = useCallback(
    debounce(
      (newValue: NewValue) => {
        onChangeDebounced?.(newValue);
      },
      debounceOptionsMemoized?.wait ?? 300,
      debounceOptionsMemoized?.options ||
        ({ leading: false, trailing: true } satisfies DebounceSettings),
    ),
    [debounceOptionsMemoized, onChangeDebounced],
  );

  const handleChange = (dataUrl: string) => {
    console.log("SignatureCanvas.handleChange", { dataUrl });

    const newValue: NewValue = {
      dataUrl: dataUrl,
      file: FileHelper.dataUrlToFile(dataUrl, fileName),
    };
    onChange?.(newValue);
    handleChangeDebounced(newValue);
  };

  return (
    <Stack sx={{ alignItems: "flex-end", ...sx }} gap={1} direction={{ xxs: "column", md: "row" }}>
      <Card
        sx={{
          display: "flex",
          width: "max-content",
          height: "max-content",
          borderRadius: (th) => th.shapeCustom.borderRadiusCard,
          overflow: "hidden",
        }}
        variant='outlined'
      >
        <SignatureCanvas
          ref={sigCanvasRef}
          minWidth={1}
          maxWidth={3}
          penColor='black'
          clearOnResize
          backgroundColor='white'
          canvasProps={canvasPropsComputed}
          onBegin={(e) => {
            // console.log("SignatureCanvas.onBegin", e);
          }}
          onEnd={async (e) => {
            // console.log("SignatureCanvas.onEnd", e);
            let svgDataUrl = sigCanvasRef.current?.getSignaturePad()._toSVG();
            if (!svgDataUrl) {
              return;
            }

            // Fix DPI/scaling issue in _toSVG from https://github.com/szimek/signature_pad
            // E.g. when devicePixelRatio=1.25 and canvas size is 400x200,
            // the _toSVG() returns SVG 320x160 which is 1.25 times smaller, but the content is correct.
            // Solution: post-process SVG to fix dimensions
            if (SvgHelper.isSvgDataUrl(svgDataUrl)) {
              const svgEl = SvgHelper.dataUrlToSVGElement(svgDataUrl);
              const svgAttrs = SvgHelper.parseAttributes(svgEl);
              const pixelRatio = Math.max(window.devicePixelRatio || 1, 1);
              if (
                svgAttrs.width * pixelRatio === canvasSize.width ||
                svgAttrs.height * pixelRatio !== canvasSize.height ||
                svgAttrs.viewBox.width * pixelRatio === canvasSize.width ||
                svgAttrs.viewBox.height * pixelRatio === canvasSize.height
              ) {
                SvgHelper.setDimensions(svgEl, {
                  width: canvasSize.width,
                  height: canvasSize.height,
                });
                SvgHelper.setViewBox(svgEl, {
                  minX: 0,
                  minY: 0,
                  width: canvasSize.width,
                  height: canvasSize.height,
                });
                const newSvgDataUrl = SvgHelper.SVGElementToDataUrl(svgEl);
                svgDataUrl = newSvgDataUrl;
              }
            }

            handleChange(svgDataUrl);
          }}
        />
      </Card>

      {withClear && (
        <Box>
          <AppButton
            variant='outlined'
            color='text'
            startIcon={<AppIcon of='clear' />}
            onClick={() => {
              sigCanvasRef.current?.clear();
              onClear?.();
            }}
          >
            Clear
          </AppButton>
        </Box>
      )}
    </Stack>
  );
}
